import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ActionIcon, Menu } from '@mantine/core';
import {
  IconBuildingBank,
  IconCircleMinus,
  IconDots,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
} from '@tabler/icons-react';
import LotStatus, { type ExchangeStatusesState } from '@/components/app/sell/LotStatus';
import BestPriceLink from '@/components/common/BestPriceLink';
import { toPercent } from '@/components/common/PrepaymentBadge';
import { bestOfferLink, bestPriceTone } from '@/lib/bestOffer';
import { exchangeUiState, lotActions } from '@/lib/exchangeOffer';
import { formatMoney } from '@/lib/format';
import { loadingWindow, mainQuality } from '@/lib/sellOffer';
import type { MyBestOffer } from '@/types/bestPrice';
import type { ExchangeSession, SaleExchangeStatusInfo, SaleOrder } from '@/types/sell';
import touch from '@/components/common/touchTarget.module.css';
import classes from './SellLotsTable.module.css';

export type { ExchangeStatusesState } from '@/components/app/sell/LotStatus';

export interface SellLotActions {
  readonly onSuspend: (id: number) => void;
  readonly onUnsuspend: (id: number) => void;
  readonly onDelete: (id: number) => void;
  readonly onPublishExchange: (id: number) => void;
  readonly onRemoveExchange: (id: number) => void;
  /** Идёт действие именно по этому лоту: кнопка меню показывает загрузку. */
  readonly pendingId: (id: number) => boolean;
  /** Идёт любое действие: меню остальных лотов недоступно, пока оно не завершится. */
  readonly busy?: boolean;
}

interface SellLotsTableProps {
  /** Культура группы: входит в имя ссылки строки, чтобы программа чтения с экрана различала лоты. */
  readonly goods: string;
  readonly lots: readonly SaleOrder[];
  readonly offerUrl: (offer: SaleOrder) => string;
  readonly editUrl: (offer: SaleOrder) => string;
  readonly bestOffers?: ReadonlyMap<number, MyBestOffer>;
  readonly exchangeStatuses?: Readonly<Record<number, SaleExchangeStatusInfo>>;
  readonly exchangeState: ExchangeStatusesState;
  readonly session?: ExchangeSession;
  readonly actions: SellLotActions;
}

// Лоты одной культуры базиса таблицей (образец b-m1-sell, «Бюллетень»): качество, вес, отгрузка, предоплата, моя цена,
// лучшая встречная цена, статус и меню действий. Вся строка ведёт на предложение; меню и кнопка «Продать» подняты
// над растянутой ссылкой. На телефоне строка становится блоком с подписями, пустые ячейки не выводятся.
export default function SellLotsTable({ lots, ...rest }: SellLotsTableProps) {
  const { t } = useTranslation();

  return (
    <table className={classes.table}>
      {/* Общие ширины колонок: таблицы культур одного базиса стоят друг под другом и должны совпадать по колонкам. */}
      <colgroup>
        <col className={classes.colQuality} />
        <col className={classes.colWeight} />
        <col className={classes.colWindow} />
        <col className={classes.colPrepayment} />
        <col className={classes.colPrice} />
        <col className={classes.colBest} />
        <col className={classes.colStatus} />
        <col className={classes.colMenu} />
      </colgroup>
      <thead>
        <tr>
          <th>{t('app.sell.table.quality')}</th>
          <th className={classes.num}>{t('app.sell.table.weight')}</th>
          <th>{t('app.sell.table.window')}</th>
          <th>{t('app.sell.table.prepayment')}</th>
          <th className={classes.num}>{t('app.sell.myPricePerTon')}</th>
          <th className={classes.num}>{t('app.sell.table.best')}</th>
          <th>{t('app.sell.table.status')}</th>
          <th>
            <span className={classes.hidden}>{t('app.sell.lot.actions')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {lots.map((offer) => (
          <LotRow key={offer.id} offer={offer} {...rest} />
        ))}
      </tbody>
    </table>
  );
}

type LotRowProps = Omit<SellLotsTableProps, 'lots'> & { readonly offer: SaleOrder };

function LotRow({ goods, offer, offerUrl, editUrl, bestOffers, exchangeStatuses, exchangeState, session, actions }: LotRowProps) {
  const { t } = useTranslation();
  const tons = t('app.sell.tons');
  const rub = t('app.deals.rubPerTon');
  const quality = mainQuality(offer);
  const window = loadingWindow(offer, (date) => t('app.sell.windowFrom', { date }));
  const percent = toPercent(offer.prepaymentInfo?.percent ?? offer.prepaymentInfo?.type ?? offer.prepaymentInfo?.name);
  const prepayment = percent == null ? null : percent === 0 ? t('app.sell.prepayment0Short') : `${percent}\u00a0%`;
  const best = bestOffers?.get(offer.id);
  const weight = offer.weightTon != null ? `${formatMoney(offer.weightTon)}\u00a0${tons}` : null;
  const price = offer.price != null ? `${formatMoney(offer.price)}\u00a0${rub}` : null;

  // Пока статус биржевого лота неизвестен (грузится или запрос не удался), действий нет: живая заявка выглядела бы
  // редактируемой, и меню открыло бы правку и удаление, которые биржа запрещает.
  const resolved = !offer.toExchange || exchangeState === 'ready';
  const exState = resolved ? exchangeUiState(offer, exchangeStatuses?.[offer.id]) : null;
  const can = lotActions(offer, exState, resolved);
  const hasMenu = Object.values(can).some(Boolean);
  const describe = [weight, price].filter(Boolean).join(', ');
  const year = offer.cropYear ? t('app.sell.cropYear', { year: offer.cropYear }) : null;

  return (
    <tr className={classes.row}>
      <td className={classes.quality}>
        <Link to={offerUrl(offer)} className={classes.link} aria-label={[goods, quality, year, describe].filter(Boolean).join(', ')}>
          {quality ?? goods}
        </Link>
        {/* Год урожая необязателен (решение 2026-11-04): строка лота показывает его, только если он задан. */}
        {year ? <span className={classes.year}> · {year}</span> : null}
      </td>
      <td className={classes.num} data-label={t('app.sell.table.weight')}>
        {offer.weightTon != null ? formatMoney(offer.weightTon) : '—'}
      </td>
      <td className={window ? classes.nowrap : classes.blank} data-label={t('app.sell.table.window')}>
        {window}
      </td>
      <td className={prepayment ? undefined : classes.blank} data-label={t('app.sell.table.prepayment')}>
        {prepayment}
      </td>
      <td className={`${classes.num} ${classes.price}`} data-label={t('app.sell.table.myPrice')}>
        {offer.price != null ? formatMoney(offer.price) : '—'}
      </td>
      <td className={best?.bestPrice != null ? classes.best : classes.blank} data-label={t('app.sell.table.bestShort')}>
        {best?.bestPrice != null ? (
          <BestPriceLink
            to={bestOfferLink(best)}
            price={best.bestPrice}
            action="sell"
            tone={bestPriceTone(best.advantage)}
            advantage={best.advantage}
          />
        ) : null}
      </td>
      <td data-label={t('app.sell.table.status')}>
        <LotStatus offer={offer} state={exState} exchangeState={exchangeState} />
      </td>
      <td className={classes.menu}>
        {hasMenu ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="default"
                size={32}
                className={`${classes.menuButton} ${touch.target}`}
                aria-label={describe ? t('app.sell.lot.actionsFor', { lot: describe }) : t('app.sell.lot.actions')}
                loading={actions.pendingId(offer.id)}
                disabled={actions.busy && !actions.pendingId(offer.id)}
              >
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {can.edit ? (
                <Menu.Item component={Link} to={editUrl(offer)} leftSection={<IconPencil size={16} />}>
                  {t('app.sell.lot.edit')}
                </Menu.Item>
              ) : null}
              {can.resume ? (
                <Menu.Item leftSection={<IconPlayerPlay size={16} />} onClick={() => actions.onUnsuspend(offer.id)}>
                  {t('app.sell.lot.resume')}
                </Menu.Item>
              ) : null}
              {can.suspend ? (
                <Menu.Item leftSection={<IconPlayerPause size={16} />} onClick={() => actions.onSuspend(offer.id)}>
                  {t('app.sell.lot.suspend')}
                </Menu.Item>
              ) : null}
              {can.publish ? (
                <Menu.Item
                  leftSection={<IconBuildingBank size={16} />}
                  disabled={!session?.open}
                  onClick={() => actions.onPublishExchange(offer.id)}
                >
                  {t(exState === 'FAILED' ? 'app.sell.exchange.republish' : 'app.sell.exchange.publish')}
                </Menu.Item>
              ) : null}
              {can.publish && session && !session.open ? (
                <Menu.Label>
                  {t('app.sell.exchange.windowClosedHint', { start: session.windowStart, end: session.windowEnd })}
                </Menu.Label>
              ) : null}
              {can.removeFromExchange ? (
                <Menu.Item color="red" leftSection={<IconCircleMinus size={16} />} onClick={() => actions.onRemoveExchange(offer.id)}>
                  {t('app.sell.exchange.removeAction')}
                </Menu.Item>
              ) : null}
              {can.remove ? (
                <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={() => actions.onDelete(offer.id)}>
                  {t('app.sell.lot.delete')}
                </Menu.Item>
              ) : null}
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </td>
    </tr>
  );
}
